import { useEffect, useRef } from 'react';

const values = [
  {
    icon: 'ri-hammer-fill',
    title: 'Precision Craftsmanship',
    desc: 'Every project is executed with meticulous attention to detail — no shortcuts, no compromises.',
  },
  {
    icon: 'ri-sparkling-fill',
    title: 'Clean Job Sites',
    desc: 'We respect your home. Job sites are kept clean and organized throughout the entire project.',
  },
  {
    icon: 'ri-time-fill',
    title: 'Reliable Timelines',
    desc: 'We outline timelines upfront and work efficiently to complete projects on schedule.',
  },
  {
    icon: 'ri-chat-check-fill',
    title: 'Direct Communication',
    desc: 'The owner is present on every job. You always know who to call and what to expect.',
  },
  {
    icon: 'ri-building-4-fill',
    title: 'Long-Term Durability',
    desc: 'Quality materials and proven techniques built to stand the test of time.',
  },
  {
    icon: 'ri-shield-check-fill',
    title: 'Licensed & Insured',
    desc: 'All work is licensed, bonded, and insured. Your home and investment are fully protected.',
  },
];

export default function WhyChooseUs() {
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateX(0)';
          }
        });
      },
      { threshold: 0.12 }
    );
    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="about" className="py-32 bg-[#FAFAF8] relative overflow-hidden">
      {/* Decorative background element */}
      <div
        className="absolute -right-32 top-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #B8956A, transparent)' }}
      ></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">

          {/* Left: Image stack */}
          <div
            ref={leftRef}
            className="relative"
            style={{ opacity: 0, transform: 'translateX(-40px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            {/* Main image */}
            <div className="w-full h-[580px] overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/53d057ff3d9a9f6ff787ff3085edaaf5.jpg"
                alt="Quality craftsmanship by Culp Home Improvement"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Floating stat card — bottom right */}
            <div
              className="absolute -bottom-8 -right-8 p-7 z-10"
              style={{ background: '#B8956A' }}
            >
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-white text-sm"></i>
                ))}
              </div>
              <p className="font-black text-2xl text-white leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>5-Star</p>
              <p className="text-white/75 text-xs mt-0.5 font-semibold uppercase tracking-widest" style={{ fontFamily: 'Montserrat, sans-serif' }}>Rated Contractor</p>
            </div>

            {/* Floating badge — top left */}
            <div
              className="absolute -top-5 -left-5 px-5 py-3.5 z-10"
              style={{ background: '#1A1A1A' }}
            >
              <p className="text-[#C9A87C] text-[10px] font-bold uppercase tracking-[0.18em]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Licensed • Bonded • Insured
              </p>
            </div>

            {/* Gold accent line */}
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#B8956A]"></div>
          </div>

          {/* Right: Content */}
          <div
            ref={rightRef}
            style={{ opacity: 0, transform: 'translateX(40px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            <p
              className="text-[#B8956A] text-[11px] font-bold uppercase tracking-[0.22em] mb-5"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Why Choose Us
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4 leading-tight"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Built on Quality,<br />Not Shortcuts
            </h2>
            <div className="flex items-center gap-4 mb-12">
              <div className="w-14 h-px bg-[#B8956A]"></div>
              <p className="text-[#888] text-sm" style={{ fontFamily: 'Lora, serif' }}>
                Every project. Every time.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
              {values.map((v, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 group p-4 hover:bg-white transition-colors duration-300 border border-transparent hover:border-[#E8E0D8]"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#B8956A]/10 flex-shrink-0 group-hover:bg-[#B8956A] transition-colors duration-300">
                    <i className={`${v.icon} text-[#B8956A] text-base group-hover:text-white transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <h4
                      className="font-bold text-[#1A1A1A] text-sm mb-1.5"
                      style={{ fontFamily: 'Montserrat, sans-serif' }}
                    >
                      {v.title}
                    </h4>
                    <p
                      className="text-[#888] text-sm leading-relaxed"
                      style={{ fontFamily: 'Lora, serif' }}
                    >
                      {v.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => scrollTo('contact')}
              className="inline-flex items-center gap-3 text-white font-bold px-9 py-4 text-sm tracking-[0.12em] uppercase transition-all duration-300 cursor-pointer whitespace-nowrap hover:-translate-y-0.5"
              style={{
                fontFamily: 'Montserrat, sans-serif',
                background: '#1A1A1A',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#B8956A'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1A1A1A'; }}
            >
              Get a Free Estimate <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
